import { useAuth0 } from "@auth0/auth0-react";
import { Box, Text, Loader, Stack, Flex, Tooltip, Menu, TextInput } from "@mantine/core";
import { openConfirmModal } from "@mantine/modals";
import {
  useDebouncedValue,
  useDisclosure,
  useElementSize,
} from "@mantine/hooks";
import { IconCheck, IconDotsVertical, IconCopy, IconX, IconTrash, IconColumns, IconPencil } from "@tabler/icons";
import { useContext, useEffect, useState, useMemo, useCallback, useRef } from "react";
import { canEditTeamIds } from "../../Api";
import { loadListMembers, getMergedColumns } from "./ideasHelpers";
import { TEST_IDS } from "./constants";

import { getFixedDateRanges } from "./filters/FixedDateRanges";
import { noTrendFilterSpec } from "./filters/FiltersSpec";
import _ from "lodash";
import { showNotification } from "@mantine/notifications";
import Header from "./components/Header";
import FilterActions from "./components/FilterActions";
import { useIdeas } from "../../hooks/useIdeas";
import { useEvidenceInclusion } from "../../hooks/useEvidenceInclusion";
import FiltersContext, {
  FiltersContextProvider,
} from "./filters/context/FiltersContext";
import { useNavigate } from "react-router-dom";
import IdeasTable from "./Table/IdeasTable";
import { BgColorRouteContext } from "../../context/BgColorContext";
import { useLocation } from "react-router-dom";
import { getConnectedUserRole, updateAccessorByState } from "../../utils";
import { DataTableSortStatus } from "mantine-datatable";
import {
  isAiView,
  buildAiViewColumns,
  readAiOverrides,
  toPersistShape,
  AI_VIEW_STORAGE_KEY,
} from "../../utils/viewUtils";
import {useIdeaFilters} from "../../hooks/useIdeaFilters";
import StickyFilters from "./components/sticky-filters/StickyFilters";
import {useFields} from "../../hooks/useFields";
import {mergeFilterLayers} from "../../utils/ideasUrlState";
import {findFilterByName, findFiltersByPath} from "../../context/helpers";
import {useIdeasPageViewSync} from "../../hooks/useIdeasPageViewSync";
import {cloneIdeaPage, deleteIdeaPage, editIdeaPage, getIdeaPages} from "../../api/idea";
import EditColumnModal from "./components/EditColumnModal";
import ManageColumnsDrawer from "./components/ManageColumnsDrawer";
import { useQueryClient } from "@tanstack/react-query";
import { ideaPagesKeys, useIdeaPages } from "../../hooks/useIdeaPages";
import { useCurrentUser } from "../../hooks/useCurrentUser";

const PAGE_SIZE = 50;

export const Ideas = () => {

  const location = useLocation();

  const isAiPage: boolean = useMemo(() => location.pathname === '/ai', [location.pathname]);


  const searchParams = new URLSearchParams(location.search);
  const isArchivedRoute = location.pathname === "/ideas/archived";
  const { ref } = useElementSize();
  const { ref: warpRef, width: warpWidth } = useElementSize();
  const { ref: actionIconsRef, width: actionIconsWidth } = useElementSize();

  const { mainPageName, fieldsLoaded, refreshSubPages } = useFields();
  const { ideasFiltersConfig } = useIdeaFilters();
  const queryClient = useQueryClient();
  const { pages: subPages } = useIdeaPages();

  const { activeFilters, setActiveFilters, setCheckedComponents, subPageKey, editMode, setEditMode } =
    useContext(FiltersContext);


  const [search, setSearch] = useState<string>(searchParams.get("search") || "");
  const [isInitializing, setIsInitializing] = useState(true);
  const prevSubPageKeyRef = useRef<string | undefined>(subPageKey);
  // Gating ref: true from the render where subPageKey changes until the merge
  // effect has computed fresh filters for the new subpage and cleared it.
  // Must be a ref (not state) so it is visible in the same render it is set.
  const selectedViewIsStaleRef = useRef(false);
  const prevSubPageKeyForStaleRef = useRef<string | undefined>(subPageKey);
  if (prevSubPageKeyForStaleRef.current !== subPageKey) {
    prevSubPageKeyForStaleRef.current = subPageKey;
    selectedViewIsStaleRef.current = true;
  }
  const prevGlobalFiltersRef = useRef<typeof globalFilters | null>(null);
  const hasProcessedGlobalFiltersRef = useRef<boolean>(false);
  const keyJustSetByRenameRef = useRef<string | null>(null);
  // Holds filters saved before navigating into an idea, used to suppress the re-merge on back navigation
  const restoredFiltersRef = useRef<any[] | null>(null);

  const [isArchived, setIsArchived] = useState(isArchivedRoute);
  const [isEditColumnModal, setIsEditColumnModal] = useState(false);
  const [selectedColumn, setSelectedColumn] = useState(null);
  const [selectedColumnAnchorRect, setSelectedColumnAnchorRect] = useState<DOMRect | undefined>(undefined);

  const [isManageColumnsDrawer, setIsManageColumnsDrawer] = useState(false);
  const [saveViewModalOpened, setSaveViewModalOpened] = useState(false);
  /** Bumps after schema-changing column add so IdeasTable remounts and mantine-datatable column store re-syncs. */
  const [ideasTableLayoutKey, setIdeasTableLayoutKey] = useState(0);

  const handleOpenSaveViewModal = () => {
    setSaveViewModalOpened(true);
  };

  // Evidence inclusion state
  const { evidenceInclusion } = useEvidenceInclusion();

  // Initialize selectedView as null - will be set by useIdeasPageViewSync hook
  const [selectedViewState, setSelectedViewState] = useState<any>(null);
  const [savedViews, setSavedViews] = useState<any>([]);
  const [tableColumns, setTableColumns] = useState<any>([]);
  const [aiTableColumns, setAiTableColumns] = useState<any>([]);

  // Tracks whether the user has explicitly clicked a column header to sort during an active search.
  // When false and search is active, no sort is sent to the backend (results sorted by search score).
  const [isExplicitSort, setIsExplicitSort] = useState(false);

  // Get ideas data using our custom hook - now reads page, sortStatus, selectedViewName from URL internally
  const {
    ideas,
    loading,
    aggregationsLoading,
    error,
    totalCount,
    refetch,
    starIdea,
    views,
    viewsLoading,
    upsertView,
    columns,
    columnsLoading,
    clearCache,
    invalidateFilters,
    invalidateSubPageCache,
    getCacheKey,
    subPage,
    subPageLoading,
    // URL state values and setters
    page,
    sortStatus,
    effectiveSortStatus,
    selectedViewName,
    globalFilters,
    tabFilters,
    setPage,
    setSortStatus,
    setSelectedViewName,
    setGlobalFilters,
    setTabFilters,
  } = useIdeas({
    search,
    activeFilters: activeFilters,
    isArchived,
    selectedView: selectedViewState,
    subPageKey,
    pathname: location.pathname,
    evidenceInclusion,
    isInitializing,
    selectedViewIsStale: selectedViewIsStaleRef.current,
    isExplicitSort,
  });

  // Use hook to sync selected view with URL and handle initialization
  const { selectedView, setSelectedView } = useIdeasPageViewSync(
    views,
    selectedViewName,
    setSelectedViewName,
    subPageKey || null,
    subPage,
    subPageLoading,
    setSavedViews
  );

  // Sync selectedViewState with selectedView from hook
  // Only update if the view ID or name actually changed to prevent unnecessary rerenders
  useEffect(() => {
    const currentId = selectedViewState?._id;
    const currentName = selectedViewState?.name;
    const currentLevel = selectedViewState?.level;
    const newId = selectedView?._id;
    const newName = selectedView?.name;
    const newLevel = selectedView?.level;

    if (currentId !== newId || currentName !== newName || currentLevel !== newLevel) {
      setSelectedViewState(selectedView);
    }
  }, [selectedView, selectedViewState]);

  // Track initialized subpages to prevent redundant initializations
  // const initializedSubPages = useRef<Set<string>>(new Set());

  // Debug the component data
  if (process.env.NODE_ENV === 'development') {
    /* console.log('🔍 Ideas Component Debug:', {
      columns: columns?.length || 0,
      columnsLoading,
      subPage: subPage,
      subPageKey,
      location: location.pathname,
      search,
      loading,
      cacheKey: getCacheKey()
    }); */
  }

  // Invalidate cache when subPageKey changes (for debugging purposes)
  useEffect(() => {
    if (process.env.NODE_ENV === 'development' && subPageKey) {
      /* console.log('🔄 SubPageKey changed to:', subPageKey, '- cache should auto-invalidate'); */
    }
  }, [subPageKey]);

  // Utility function for manual cache management (development only)
  const handleClearCache = useCallback(() => {
    if (process.env.NODE_ENV === 'development') {
      clearCache();
    }
  }, [clearCache]);

  const handleInvalidateSubPageCache = useCallback(() => {
    if (process.env.NODE_ENV === 'development') {
      invalidateSubPageCache();
    }
  }, [invalidateSubPageCache]);

  useEffect(() => {
    setIsArchived(isArchivedRoute);
  }, [isArchivedRoute]);

  // On mount: if the user navigated back from an idea detail page (via browser back or custom
  // back button), restore the exact filter state they had before navigating in.
  // The subPageKey guard prevents stale restores when navigating to a different subpage.
  useEffect(() => {
    const stored = sessionStorage.getItem('lastActiveFilters');
    const storedSubPage = sessionStorage.getItem('lastActiveFiltersSubPage');
    sessionStorage.removeItem('lastActiveFilters');
    sessionStorage.removeItem('lastActiveFiltersSubPage');
    if (stored && storedSubPage === (subPageKey || "")) {
      try {
        const parsed = JSON.parse(stored);
        const normalized = Array.isArray(parsed)
          ? parsed.map((item: any) => {
            if (!item?.recoverableDBFieldPath) return item;
            return { ...item, dbFieldPath: item.recoverableDBFieldPath, selected: [], operator: item.recoverableOperator };
          })
          : parsed;
        restoredFiltersRef.current = normalized;
        setActiveFilters(normalized);
      } catch (e) { /* ignore parse errors */ }
    }
  }, []); // runs once on mount

  // When URL has selectedView=All, allow ideas query immediately (no view filters to wait for)
  useEffect(() => {
    if (selectedViewName && selectedViewName.toLowerCase() === 'all' && isInitializing) {
      setIsInitializing(false);
    }
  }, [selectedViewName, isInitializing]);

  // Reset explicit sort flag whenever the search text changes so the backend sorts by search score.
  // Any edit to the search query (new search or modifying existing) clears the explicit sort.
  const prevSearchRef = useRef<string>(search || '');
  useEffect(() => { 
    const prevSearch = prevSearchRef.current || '';
    const searchChanged = prevSearch !== search;

    if (searchChanged && search.trim() !== '') {
      setIsExplicitSort(false);
    }
    
    prevSearchRef.current = search || '';
  }, [search]);

  const subPageName = subPage?.label;

  const navigate = useNavigate();

  const isSubPageExists = (subPages: any[], key: string) =>
    subPages && subPages.some((page: any) => page.key === key || page.route === key)

  // Check if the current route key is valid and redirect if invalid
  useEffect(() => {
    if (!fieldsLoaded) return;

    const pathMatch = location.pathname.match(/^\/ideas\/(.+)$/);
    if (pathMatch && subPages.length > 0) {
      const routeKey = pathMatch[1];
      if (routeKey === keyJustSetByRenameRef.current) {
        keyJustSetByRenameRef.current = null;
        return;
      }

      const isValidKey =
        routeKey === "all" ||
        routeKey === "archived" ||
        isSubPageExists(subPages, routeKey);
      if (!isValidKey) {
        navigate('/ideas/all', { replace: true });
      }
    }
  }, [location.pathname, subPages, fieldsLoaded, navigate]);

  useEffect(() => {
    if (subPageKey && subPageKey !== "all" && subPageKey !== "archived" && !subPageLoading && !subPage && fieldsLoaded) {
      if (isSubPageExists(subPages, subPageKey)) {
        navigate(`/ideas/${subPageKey}`, { replace: true });
      }
    }
  }, [subPageKey, subPageLoading, subPage, fieldsLoaded, subPages, navigate]);

  const [debouncedSearch] = useDebouncedValue(search, 1000);
  const [loadingCols, setLoadingCols] = useState(false);
  const [searchOpened, setSearchOpened] = useState(false);
  const [isAllowedToEditDates, setIsAllowedToEditDates] = useState<boolean>(true);
  const [opened, { open, close: doClose }] = useDisclosure(false);
  const [viewName, setViewName] = useState("");
  const [menuFiltersObj, setMenuFiltersObj] = useState<any>(noTrendFilterSpec);
  const [filterRowOpened, { toggle }] = useDisclosure(true);
  const { setBgColor, setPageName } = useContext(BgColorRouteContext);
  const [isGlobal, setIsGlobal] = useState(selectedView?.isGlobal);
  const [isSubPageView, setIsSubPageView] = useState(selectedView?.isSubPageView);
  const [editTitle, setEditTitle] = useState(false);
  const [isTagsHovered, setIsTagsHovered] = useState<boolean>(false);
  const [sortState, setSortState] = useState<string>("total"); // For dynamic/bar cells

  const [inputValue, setInputValue] = useState(subPageName || mainPageName);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [filterModalOpened, setFilterModalOpened] = useState(false);
  const auth0 = useAuth0();
  const currentUser = useCurrentUser();
  const userRole = getConnectedUserRole(currentUser);

  // Provide default sortStatus for IdeasTable (which expects non-null)
  const defaultSortStatus: DataTableSortStatus = useMemo(() => ({
    columnAccessor: "aggregations.accountsCount.total",
    direction: "desc"
  }), []);
  
  // Use the hook's effectiveSortStatus (which validates the URL sort against
  // the active view's columns and falls back to AI_VIEW_DEFAULT_SORT for the
  // Bagel AI view) so the table's sort indicator stays in sync with what the
  // query actually sorts by. Falls back to defaultSortStatus when nothing is set.
  const tableSortStatus: DataTableSortStatus = useMemo(() =>
    effectiveSortStatus || defaultSortStatus,
  [effectiveSortStatus, defaultSortStatus]
  );

  // Wrapper for setSortStatus that matches IdeasTable's expected type.
  // Also marks the sort as user-explicit so it is included in API calls even during active search.
  const setTableSortStatus = useCallback((value: DataTableSortStatus | ((prev: DataTableSortStatus) => DataTableSortStatus) | null) => {
    setIsExplicitSort(true);
    if (typeof value === 'function') {
      setSortStatus((prev) => {
        const current = prev || defaultSortStatus;
        const result = value(current);
        return result;
      });
    } else if (_.isNull(value)) {
      setSortStatus(defaultSortStatus);
    } else {
      setSortStatus(value);
    }
  }, [setSortStatus, defaultSortStatus]);

  // Sync sortStatus.columnAccessor when evidenceInclusion changes
  // This ensures the sort indicator stays on the correct column when toggling "Validated only"
  const prevEvidenceInclusionRef = useRef(evidenceInclusion);
  useEffect(() => {
    if (prevEvidenceInclusionRef.current !== evidenceInclusion && sortStatus?.columnAccessor) {
      // Transform the current sort accessor to use the new evidenceInclusion state
      const newAccessor = updateAccessorByState(sortStatus.columnAccessor, evidenceInclusion);
      if (newAccessor !== sortStatus.columnAccessor) {
        setSortStatus((prev: any) => ({
          ...prev,
          columnAccessor: newAccessor
        }));
      }
      prevEvidenceInclusionRef.current = evidenceInclusion;
    }
  }, [evidenceInclusion, sortStatus?.columnAccessor, setSortStatus, defaultSortStatus]);

  useEffect(() => {
    setInputValue(subPageName || mainPageName || 'Main')
  }, [subPageName, mainPageName])

  // useEffect(() => {
  //   // Clear initialized tracking when subPageKey changes
  //   initializedSubPages.current.clear();
  // }, [subPageKey]);

  useEffect(() => {
    setBgColor("#ffffff");
    setPageName("idea");
  }, [setBgColor]);

  const handleSaveViews = async (data:any) => {
    try {
      const res = await upsertView({ 
        ...(data?._id ? { _id: data._id } : {}),
        name: data.name, 
        tooltip: data.tooltip || '',
        filters: activeFilters, 
        subPageKey: subPageKey, 
        isGlobal: userRole === "admin" ? data.isGlobal : false, 
        isSubPageView: userRole === "admin" ? data.isSubPageView : false,
        operator: data.operator || 'AND',
        color: data.color,
        level: data.level,
      });
      const newViewId = res?.data?.updateOneResult?.upsertedId;

      if (newViewId) {
        if (subPageKey) {
          localStorage.setItem(`ideas-view-${subPageKey}`, newViewId);
        } else {
          localStorage.setItem('ideas-view-all', newViewId);
        }
      }
      setSelectedViewName(data.name);
      setViewName("");
      showNotification({
        title: "Saved successfully!",
        color: "teal",
        message: "view saved successfully",
        icon: <IconCheck size="1.1rem" />,
        autoClose: 1200,
      });
      doClose();
    } catch (error) {
      console.error("Failed to save view:", error);
    }
  };

  const loadCanEditTeamIds = () => {
    canEditTeamIds(auth0)
      .then((customizations: any) => {
        const name =
          customizations && customizations.length > 0
            ? customizations[0].name
            : undefined;
        if (name === "committedDate") {
          setIsAllowedToEditDates(true);
        } else {
          setIsAllowedToEditDates(false);
        }
      })

      .catch((e) => {
        console.log(e);
      });
  };

  // Simplified effects - React Query handles most of the data fetching logic
  useEffect(() => {
    setMenuFiltersObj(ideasFiltersConfig);
  }, [ideasFiltersConfig]);

  useEffect(() => {
    if (debouncedSearch !== search) {
      // Reset explicit sort flag while user is still typing (debounce in progress)
      setIsExplicitSort(false);
    }
  }, [debouncedSearch]);

  const convertGlobalFiltersToActiveFilters = useCallback((globalFilters: any, ideasFiltersConfig: any): any[] => {
    const globalFiltersArray: any[] = [];

    const buildSelectedFilterMap = (selectedValues: string[], options: any): Record<string, string> | undefined => {
      if (!Array.isArray(selectedValues) || selectedValues.length === 0) return undefined;
      const lookup = new Map<string, string>();

      const visit = (opt: any) => {
        if (!opt || typeof opt !== 'object') return;
        const label =
          (typeof opt.label === 'string' && opt.label) ||
          (typeof opt.name === 'string' && opt.name) ||
          '';
        const keys = [opt.value, opt._id, opt.id]
          .filter((k) => k !== undefined && k !== null && String(k).trim() !== '')
          .map((k) => String(k));
        if (label) {
          keys.forEach((k) => {
            lookup.set(k, label);
            lookup.set(k.toLowerCase(), label);
          });
        }
        if (Array.isArray(opt.childComponents)) {
          opt.childComponents.forEach(visit);
        }
      };

      if (Array.isArray(options)) {
        options.forEach(visit);
      } else if (options && typeof options === 'object') {
        Object.entries(options).forEach(([k, v]: [string, any]) => {
          if (typeof v === 'string') {
            lookup.set(k, v);
            lookup.set(k.toLowerCase(), v);
            return;
          }
          visit(v);
        });
      }

      const out: Record<string, string> = {};
      selectedValues.forEach((raw) => {
        const id = String(raw);
        const mapped = lookup.get(id) || lookup.get(id.toLowerCase());
        if (mapped) out[id] = mapped;
      });
      return Object.keys(out).length > 0 ? out : undefined;
    };
    
    if (globalFilters?.owner && globalFilters.owner.length > 0) {
      const ownerFilterConfig = findFilterByName('owner', 'all', ideasFiltersConfig);
      if (ownerFilterConfig) {
        const selectedFilterMap = buildSelectedFilterMap(globalFilters.owner, ownerFilterConfig.options);
        globalFiltersArray.push({
          ...ownerFilterConfig,
          selected: globalFilters.owner,
          parent: ownerFilterConfig.name || 'owner',
          ...(selectedFilterMap && { selectedFilterMap })
        });
      }
    }
    
    if (globalFilters?.productArea && globalFilters.productArea.length > 0) {
      const productAreaFilterConfig = findFilterByName('product-area', 'all', ideasFiltersConfig);
      if (productAreaFilterConfig) {
        const selectedFilterMap = buildSelectedFilterMap(globalFilters.productArea, productAreaFilterConfig.options);
        globalFiltersArray.push({
          ...productAreaFilterConfig,
          selected: globalFilters.productArea,
          parent: productAreaFilterConfig.name || 'product-area',
          ...(selectedFilterMap && { selectedFilterMap })
        });
      }
    }
    
    if (globalFilters?.dateRange) {
      // Find the "Created At" date filter in the evidence section (same logic as CreatedAtFiltersIdeas)
      const cfgAny: any = ideasFiltersConfig as any;
      const cfg = (cfgAny?.evidence) || (cfgAny?.data?.evidence) || {};
      const keys: string[] = Object.keys(cfg || {});
      // Prefer keys that look like "created at"
      const preferred = keys.find((k) => {
        const f = cfg[k];
        const label = (f?.label || k || '').toString().toLowerCase();
        return f?.type === 'date' && (label.includes('created'));
      });
      const createdAtKey = preferred || keys.find((k) => cfg[k]?.type === 'date') || 'createdAt';
      
      // Get the filter config using findFiltersByPath, with fallback to direct access
      let dateRangeFilterConfig = findFiltersByPath(ideasFiltersConfig, `evidence.${createdAtKey}`);
      if (!dateRangeFilterConfig && cfg[createdAtKey]) {
        dateRangeFilterConfig = cfg[createdAtKey];
      }

      if (dateRangeFilterConfig) {
        globalFiltersArray.push({
          ...dateRangeFilterConfig,
          filterObject: 'evidence',
          name: dateRangeFilterConfig.name || createdAtKey,
          selected: [globalFilters.dateRange.start, globalFilters.dateRange.end],
          parent: createdAtKey,
          fieldPath: 'createdAt',
          dbFieldPath: dateRangeFilterConfig.dbFieldPath || 'createdAt',
          query: null
        });
      }
    }
    
    return globalFiltersArray;
  }, []);

  // Create a stable reference for dateRange to ensure effect triggers on date changes
  const dateRangeKey = useMemo(() => {
    if (!globalFilters?.dateRange) return null;
    return `${globalFilters.dateRange.start}_${globalFilters.dateRange.end}`;
  }, [globalFilters?.dateRange?.start, globalFilters?.dateRange?.end]);

  /** Tracks the JSON of the last filters array computed by the merge effect, so we
   *  can skip redundant setActiveFilters calls without reading activeFilters (which
   *  would put it in the dep array and cause user-applied filters to be overwritten). */
  const lastMergedFiltersStringRef = useRef<string>('');

  useEffect(() => {
    const subPageKeyChanged = prevSubPageKeyRef.current !== subPageKey;
    prevSubPageKeyRef.current = subPageKey;

    // When subPageKey changes, selectedView is still the old subpage's view
    // (useIdeasPageViewSync sets it in its own effect whose setState won't be
    // visible until the next render). Computing merged filters now would use
    // wrong view filters. Instead: wipe activeFilters so the old subpage's
    // filters are gone immediately
    if (subPageKeyChanged) {
      // Don't touch activeFilters here. Clearing it to [] would re-trigger this
      // effect (activeFilters is a dep), and that second run sees globalFiltersMissing=true
      // which causes a merge with the still-stale selectedView — wrong filters would be set.
      // selectedViewIsStaleRef already gates the query.
      lastMergedFiltersStringRef.current = '';
      setPage('1');                
      return;
    }

    // Track globalFilters changes - check if globalFilters has actual values
    const hasGlobalFilters = globalFilters && (
      (globalFilters.owner && globalFilters.owner.length > 0) ||
      (globalFilters.productArea && globalFilters.productArea.length > 0) ||
      (globalFilters.dateRange !== null && globalFilters.dateRange !== undefined)
    );
    
    // Check if globalFilters were just loaded (prevGlobalFiltersRef is null on first run)
    const isFirstRun = prevGlobalFiltersRef.current === null;
    const globalFiltersChanged = JSON.stringify(prevGlobalFiltersRef.current) !== JSON.stringify(globalFilters);
    prevGlobalFiltersRef.current = globalFilters;

    const currentViewId = selectedView?._id || selectedView?.id || null;
    const currentViewFiltersLength = selectedView?.filters?.length || 0;
    const viewChanged = 
      lastSelectedViewRef.current.id !== currentViewId ||
      lastSelectedViewRef.current.filtersLength !== currentViewFiltersLength;

    // Check if ideasFiltersConfig is loaded - needed to convert globalFilters
    const hasFiltersConfig = !!ideasFiltersConfig;

    // If no filters config yet and we have global filters, wait for it to load
    if (!hasFiltersConfig && hasGlobalFilters) {
      return;
    }

    // If no view filters and no global filters, just update state and return
    if (!selectedView?.filters && !hasGlobalFilters) {
      setPage("1");
      setIsGlobal(selectedView?.isGlobal);
      setIsSubPageView(selectedView?.isSubPageView);
      // Update ref even if no filters to track the state
      if (viewChanged) {
        lastSelectedViewRef.current = { id: currentViewId, filtersLength: currentViewFiltersLength };
      }
      // The current subpage simply has no filters to merge. This is a settled
      // state for the new subpage, so ungate the query.
      selectedViewIsStaleRef.current = false;
      // Mark initialization as complete if we're initializing
      if (isInitializing) {
        setIsInitializing(false);
      }
      return;
    }

    // Track if we've processed global filters before
    // This helps detect when globalFilters are loaded from persisted store after initial render
    const wasProcessingGlobalFilters = hasProcessedGlobalFiltersRef.current;
    if (hasGlobalFilters && !hasProcessedGlobalFiltersRef.current) {
      hasProcessedGlobalFiltersRef.current = true;
    }
    
    // Check if global filters are missing from current activeFilters
    // This handles the case where globalFilters load after activeFilters are already set
    let globalFiltersMissing = false;
    if (hasGlobalFilters && activeFilters && activeFilters.length > 0) {
      // Check each type of global filter
      if (globalFilters.owner && globalFilters.owner.length > 0) {
        const hasOwnerFilter = activeFilters.some((f: any) => 
          (f.parent?.toLowerCase() === 'owner' || f.name?.toLowerCase() === 'owner') && 
          Array.isArray(f.selected) && 
          f.selected.length > 0
        );
        if (!hasOwnerFilter) {
          globalFiltersMissing = true;
        }
      }
      if (globalFilters.productArea && globalFilters.productArea.length > 0) {
        const hasProductAreaFilter = activeFilters.some((f: any) => 
          (f.parent?.toLowerCase() === 'product-area' || f.name?.toLowerCase() === 'product-area') && 
          Array.isArray(f.selected) && 
          f.selected.length > 0
        );
        if (!hasProductAreaFilter) {
          globalFiltersMissing = true;
        }
      }
      if (globalFilters.dateRange) {
        const dateRange = globalFilters.dateRange;
        const hasDateFilter = activeFilters.some((f: any) => 
          f.type === 'date' && 
          Array.isArray(f.selected) && 
          f.selected.length === 2 &&
          f.selected[0] === dateRange.start &&
          f.selected[1] === dateRange.end
        );
        if (!hasDateFilter) {
          globalFiltersMissing = true;
        }
      }
    } else if (hasGlobalFilters && (!activeFilters || activeFilters.length === 0)) {
      // If we have global filters but no activeFilters at all, they're definitely missing
      globalFiltersMissing = true;
    }
    
    // Allow processing if globalFilters changed, ideasFiltersConfig loaded, or view/subPage changed
    // Always process on initialization, when globalFilters are first loaded, or when they're missing from activeFilters
    // selectedViewIsStaleRef.current is true from the render where subPageKey changes until
    // a successful merge clears it
    const shouldProcess = 
      isInitializing ||
      viewChanged || 
      selectedViewIsStaleRef.current || // force processing after any subpage switch
      globalFiltersChanged ||
      (hasGlobalFilters && !wasProcessingGlobalFilters) || // Process when globalFilters first become available
      globalFiltersMissing; // Process if global filters are missing from activeFilters

    // If nothing changed and we're not initializing, skip processing
    if (!shouldProcess && lastSelectedViewRef.current.id !== null) {
      return;
    }

    // When navigating back from an idea, suppress the initial merge so that view filters the user
    // had removed are not re-added. The restored filters from sessionStorage take precedence.
    // Keep suppressing until the view's filters have been seen (isFirstViewLoad), then consume.
    if (restoredFiltersRef.current !== null) {
      const isFirstViewLoad = viewChanged && lastSelectedViewRef.current.id === null && currentViewId !== null;
      const isViewLoadedWithNoFilters = selectedView !== null && !(selectedView?.filters?.length > 0);
      if (isInitializing || isFirstViewLoad) {
        if (isFirstViewLoad || isViewLoadedWithNoFilters) restoredFiltersRef.current = null;
        lastSelectedViewRef.current = { id: currentViewId, filtersLength: currentViewFiltersLength };
        setIsGlobal(selectedView?.isGlobal);
        setIsSubPageView(selectedView?.isSubPageView);
        // The restored filters (from sessionStorage) are the intended filters for
        // this subpage; we're intentionally not merging. Ungate the query.
        selectedViewIsStaleRef.current = false;
        if (isInitializing) setIsInitializing(false);
        return;
      }
    }

    const globalFiltersArray = convertGlobalFiltersToActiveFilters(globalFilters, ideasFiltersConfig);
    let pageFilters = Array.isArray(subPage?.defaultFilters) ? subPage.defaultFilters : [];
    let viewFilters = (Array.isArray(selectedView?.filters) ? selectedView.filters : []).map((filter: any) =>
      filter.type == "date" && filter.operator
        ? {
          ...filter,
          selected: getFixedDateRanges[filter.operator] ? getFixedDateRanges[filter.operator].values : [],
        }
        : {
          ...filter,
        }
    );
    
    // Remove any conflicting date filters from tabFilters
    // If we have a global dateRange, remove matching date filters to ensure global filters take precedence
    // If global dateRange is null, also remove date filters to clear them
    let userFilters = tabFilters || [];
    if (ideasFiltersConfig) {
      const cfgAny: any = ideasFiltersConfig as any;
      const cfg = (cfgAny?.evidence) || (cfgAny?.data?.evidence) || {};
      const keys: string[] = Object.keys(cfg || {});
      // Find the createdAt filter key
      const preferred = keys.find((k) => {
        const f = cfg[k];
        const label = (f?.label || k || '').toString().toLowerCase();
        return f?.type === 'date' && (label.includes('created'));
      });
      const resolvedCreatedAtKey = preferred || keys.find((k) => cfg[k]?.type === 'date') || 'createdAt';

      // After Clear all, prefer activeFilters over stale tabFilters (do not re-merge from URL).
      const pageFilterNames = new Set(
        (selectedView?.filters || [])
          .filter((f: any) => f.default === true)
          .map((f: any) => (f.name || f.parent)?.toLowerCase())
          .filter(Boolean)
      );
      const viewFilterNames = new Set(
        (selectedView?.filters || [])
          .filter((f: any) => f.default !== true)
          .map((f: any) => (f.name || f.parent)?.toLowerCase())
          .filter(Boolean)
      );
      const isUserLevelTabFilter = (f: any) => {
        const filterName = (f.name || f.parent)?.toLowerCase();
        if (!filterName) return false;
        if (['owner', 'product-area', 'daterange', 'date-range'].includes(filterName)) return false;
        if (f.type === 'date' && filterName.includes('created')) return false;
        if (pageFilterNames.has(filterName) || viewFilterNames.has(filterName)) return false;
        return true;
      };
      const activeUserFilters = (activeFilters || []).filter(isUserLevelTabFilter);
      const tabUserFilters = userFilters.filter(isUserLevelTabFilter);
      if (activeUserFilters.length < tabUserFilters.length) {
        userFilters = activeUserFilters;
      }

      // Remove date filters that match the createdAt filter (whether global dateRange exists or not)
      userFilters = userFilters.filter((filter: any) => {
        if (filter.type === 'date') {
          const filterKey = (filter.parent || filter.name || '').toLowerCase();
          return filterKey !== resolvedCreatedAtKey.toLowerCase();
        }
        return true;
      });
    }

    // Owner/product-area/date-range are "global" (sticky) filters. Once the user has an
    // active sticky-filter selection for one of these fields, it must win over a page/view
    // default for that same field — otherwise a stale owner/product-area baked into a saved
    // page/view filter (e.g. via "Save as page filter", which snapshots whatever was active
    // at save time) would permanently win over mergeFilterLayers' page/view > global
    // precedence, silently ignoring any change made via the sticky filter. When the sticky
    // filter has no active selection, the page/view default is left alone so it can still
    // act as the initial default for users who haven't touched the sticky filter yet.
    const activeGlobalStickyNames = new Set(
      globalFiltersArray.map((f: any) => (f?.name || f?.parent || '').toLowerCase()).filter(Boolean)
    );
    const isOverriddenByActiveGlobalFilter = (f: any): boolean => {
      const filterName = (f?.name || f?.parent || '').toLowerCase();
      return !!filterName && activeGlobalStickyNames.has(filterName);
    };
    pageFilters = pageFilters.filter((f: any) => !isOverriddenByActiveGlobalFilter(f));
    viewFilters = viewFilters.filter((f: any) => !isOverriddenByActiveGlobalFilter(f));

    const mergedFilters = mergeFilterLayers(
      globalFiltersArray,  // Level 1: Global
      pageFilters,          // Level 2: Page
      viewFilters,          // Level 3: View
      userFilters           // Level 4: User
    );

    const mergedFiltersString = JSON.stringify(mergedFilters);
    // Compare against the last value WE computed, not against activeFilters.
    // Reading activeFilters here would put it in the dep array, causing this effect
    // to fire every time the filter modal calls setActiveFilters directly — which
    // would immediately overwrite the user's selection before Effect A (tabFilters
    // sync) can persist it to the URL.
    const shouldUpdate =
      lastMergedFiltersStringRef.current !== mergedFiltersString || globalFiltersMissing;

    if (shouldUpdate) {
      lastMergedFiltersStringRef.current = mergedFiltersString;
      setActiveFilters(mergedFilters);
      if (isInitializing) {
        setIsInitializing(false);
      }
    }
    selectedViewIsStaleRef.current = false;
    // Update ref to track current view state
    lastSelectedViewRef.current = { id: currentViewId, filtersLength: currentViewFiltersLength };
    
    setIsGlobal(selectedView?.isGlobal);
    setIsSubPageView(selectedView?.isSubPageView);
  }, [selectedView, subPageKey, globalFilters, dateRangeKey, tabFilters, ideasFiltersConfig, convertGlobalFiltersToActiveFilters, setActiveFilters, setPage, subPage, activeFilters, isInitializing]);

  const lastSelectedViewRef = useRef<{ id: string | null; filtersLength: number }>({ id: null, filtersLength: 0 });

  useEffect(() => {
    loadCanEditTeamIds();
    loadListMembers(setMenuFiltersObj, auth0);
    const queryParams = new URLSearchParams(location.search);
    const filtersStr = queryParams.get("filters");
    const filters = filtersStr
      ? JSON.parse(decodeURIComponent(filtersStr))
      : undefined;
    if (filters) {
      const hasGlobalFilters = globalFilters && (
        (globalFilters.owner && globalFilters.owner.length > 0) ||
        (globalFilters.productArea && globalFilters.productArea.length > 0) ||
        (globalFilters.dateRange !== null && globalFilters.dateRange !== undefined)
      );
      // Don't overwrite if we have global filters - let the merge effect handle it
      if (!hasGlobalFilters) {
        setActiveFilters(filters);
        setCheckedComponents((prev) => ({
          ...prev,
          ...filters.reduce(
            (acc: any, filter: any) => ({
              ...acc,
              [filter.parent.toLowerCase()]: filter?.selected?.map((i: string) =>
                i.toLowerCase()
              ),
            }),
            {}
          ),
        }));
      }
    }
  }, [globalFilters]);

  const prevMergedFiltersRef = useRef<any[]>([]);

  useEffect(() => {
    if (viewsLoading || !selectedView || !activeFilters) return;
    
    const pageFilterNames = new Set(
      (selectedView?.filters || [])
        .filter((f: any) => f.default === true)
        .map((f: any) => (f.name || f.parent)?.toLowerCase())
        .filter(Boolean)
    );
    
    const viewFilterNames = new Set(
      (selectedView?.filters || [])
        .filter((f: any) => f.default !== true)
        .map((f: any) => (f.name || f.parent)?.toLowerCase())
        .filter(Boolean)
    );
    
    const globalFilterNames = new Set(['owner', 'product-area', 'daterange', 'date-range']);
    
    // Find the createdAt filter key and exclude it from tabFilters
    // This ensures createdAt date filters are always managed via globalFilters.dateRange
    const createdAtFilterNames = new Set<string>();
    if (ideasFiltersConfig) {
      const cfgAny: any = ideasFiltersConfig as any;
      const cfg = (cfgAny?.evidence) || (cfgAny?.data?.evidence) || {};
      const keys: string[] = Object.keys(cfg || {});
      // Find all possible createdAt filter names
      keys.forEach((k) => {
        const f = cfg[k];
        if (f?.type === 'date') {
          const label = (f?.label || k || '').toString().toLowerCase();
          if (label.includes('created')) {
            createdAtFilterNames.add(k.toLowerCase());
            if (f?.name) createdAtFilterNames.add(f.name.toLowerCase());
          }
        }
      });
      // Also add common variations
      createdAtFilterNames.add('created-at');
      createdAtFilterNames.add('createdat');
      createdAtFilterNames.add('created_at');
    }
    
    // User filters are those in activeFilters that are not in global, page, or view filters
    // Always exclude createdAt date filters (they're managed via globalFilters.dateRange)
    const userFiltersForUrl = (activeFilters || []).filter((f: any) => {
      const filterName = (f.name || f.parent)?.toLowerCase();
      if (!filterName) return false;
      
      // Exclude if it's a global filter
      if (globalFilterNames.has(filterName)) return false;
      
      // Always exclude createdAt date filters (managed via globalFilters.dateRange)
      if (f.type === 'date' && createdAtFilterNames.has(filterName)) {
        return false;
      }
      
      // Exclude if it's in page or view filters
      if (pageFilterNames.has(filterName) || viewFilterNames.has(filterName)) return false;
      
      return true;
    });
    
    // Only update if user filters actually changed (to avoid infinite loops)
    const prevUserFiltersString = JSON.stringify(
      prevMergedFiltersRef.current.filter((f: any) => {
        const filterName = (f.name || f.parent)?.toLowerCase();
        if (!filterName) return false;
        
        // Exclude if it's a global filter
        if (globalFilterNames.has(filterName)) return false;
        
        // Always exclude createdAt date filters (managed via globalFilters.dateRange)
        if (f.type === 'date' && createdAtFilterNames.has(filterName)) {
          return false;
        }
        
        // Exclude if it's in page or view filters
        if (pageFilterNames.has(filterName) || viewFilterNames.has(filterName)) return false;
        
        return true;
      })
    );
    const currentUserFiltersString = JSON.stringify(userFiltersForUrl);
    
    if (prevUserFiltersString !== currentUserFiltersString) {
      setTabFilters(userFiltersForUrl);
      prevMergedFiltersRef.current = activeFilters;
    }
  }, [activeFilters, viewsLoading, selectedView, setTabFilters, globalFilters, ideasFiltersConfig]);

  // Clone handler
  const handleClone = async () => {
    const payload = {
      key: subPageKey,
      isClone: true,
    };
    try {
      const clonedIdeaPage = await cloneIdeaPage(auth0, payload);

      if (clonedIdeaPage.subPageKey) {
        await refreshSubPages(auth0);
        navigate(`/ideas/${clonedIdeaPage?.subPageKey}`, { replace: true });
        window.location.reload();
      }
    } catch (err) {
      alert('Failed to clone page');
    }
  };

  useEffect(() => {
    if (!columns.length || isAiView(selectedView)) return;
    let mergedColumns = columns;
    const columnsConfig = JSON.parse(localStorage.getItem(`ideas-columns-${subPageKey ? subPageKey : 'all'}`) || '[]');
    if (subPage && columns?.length) {
      mergedColumns = getMergedColumns(columns, subPage, columnsConfig);
    } else {
      mergedColumns = _.map(columns, (item: any) => {
        const savedCol = columnsConfig.find((col: any) => col.accessor === item.accessor);
        return savedCol ? { ...item, ...savedCol } : item;
      });
    }
    setTableColumns(mergedColumns);
  }, [columns, subPageKey, selectedView, subPage]);

  useEffect(() => {
    if (!columns.length || !isAiView(selectedView)) return;
    setAiTableColumns(buildAiViewColumns(columns, readAiOverrides()));
  }, [columns, selectedView]);

  const isAiSelected = isAiView(selectedView);
  const activeColumns = isAiSelected ? aiTableColumns : tableColumns;
  const setActiveCols = isAiSelected ? setAiTableColumns : setTableColumns;
  const activeStorageKey = isAiSelected
    ? AI_VIEW_STORAGE_KEY
    : `ideas-columns-${subPageKey ? subPageKey : 'all'}`;

  const handleSaveColumns = (updatedColumns: any[]) => {
    localStorage.setItem(activeStorageKey, JSON.stringify(toPersistShape(updatedColumns)));
    setActiveCols(updatedColumns);
  }

  const handleColumnsReordered = useCallback((orderedAccessors: string[]) => {
    const isAi = isAiView(selectedView);
    const setter = isAi ? setAiTableColumns : setTableColumns;
    const key = isAi
      ? AI_VIEW_STORAGE_KEY
      : `ideas-columns-${subPageKey ? subPageKey : 'all'}`;
    setter((prev: any[]) => {
      const accessorToIndex = new Map<string, number>();
      orderedAccessors.forEach((acc, i) => accessorToIndex.set(acc, i));

      const updated = prev.map((col: any) => {
        const newIdx = accessorToIndex.get(col.accessor);
        if (newIdx !== undefined) {
          return { ...col, index: newIdx };
        }
        return col;
      });

      localStorage.setItem(key, JSON.stringify(toPersistShape(updated)));
      return updated;
    });
  }, [subPageKey, selectedView]);

  const handleDelete = async () => {
    try {
      // Call the subpage PUT API to delete the page
      await deleteIdeaPage(auth0, {
        key: subPageKey
      });
      
      // Show success notification
      showNotification({
        title: "Page deleted successfully",
        color: "teal",
        message: "The page has been deleted",
        icon: <IconCheck size="1.1rem" />,
        autoClose: 2000
      });

      await refreshSubPages(auth0);
      
      // Redirect to /ideas
      navigate('/ideas', { replace: true });
      
    } catch (error) {      
      // Show error notification
      showNotification({
        title: "Error",
        color: "red",
        message: "Failed to delete page. Please try again.",
        icon: <IconX size="1.1rem" />,
        autoClose: 3000
      });
    }
  };

  const handleNameSave = async () => {
    if (inputValue === subPageName) {
      return;
    }
    const previousInputValue = inputValue;
    setInputValue(previousInputValue);

    try {
      const updatedIdeaPage = await editIdeaPage(auth0, {
        title: previousInputValue,
        key: subPageKey,
      });

      await refreshSubPages(auth0);
      const nextKey = updatedIdeaPage?.subPageKey ?? subPageKey;
      if (nextKey) {
        keyJustSetByRenameRef.current = nextKey;
        const subPageIndex = subPages.findIndex((page: any) => page.key === subPageKey);
        if (subPageIndex !== -1) {
          subPages[subPageIndex].key = nextKey;
          subPages[subPageIndex].label = previousInputValue;
        }
        navigate(`/ideas/${nextKey}`, { replace: true });
      }
    } catch (err) {
      alert('Failed to edit subpage');
    }
  };

  const openDeleteModal = () =>{
    openConfirmModal({
      title: <Text fz={20} fw={600} ml={20} mt={40}>Delete page</Text>,
      size: 496,
      centered: true,
      children: (
        <Text size="sm" mt={15} mb={30}>Are you sure you want to delete this page?</Text>
      ),
      styles: {
        close: {
          alignSelf: "flex-start",  
          marginTop: 10,
          marginRight: 10,
        },
        header:{
          padding: "10px 20px"
        },
        body: {
          height: 120, 
          padding: 40,
        },
        
      },
      labels: { confirm: "Delete page", cancel: "No, keep it" },
      confirmProps: {
        color: "red",
        px: 8,
        styles: {
          label: {
            fontSize: 12, 
            fontWeight: 500
          },
        },
        leftIcon: <IconTrash size={14} />,
      },
      cancelProps: {
        leftIcon: <IconCheck size={16} />,
        px: 8,
        styles: {
          label: {
            fontSize: 12, 
            fontWeight: 500
          },
        },
      },
      onCancel: () => console.log("Cancel"),
      onConfirm: () => handleDelete()
    });
  };

  const updateColumn = (updatedColumn: any) => {
    const updatedColumns = _.map(activeColumns, item => item.accessor === updatedColumn.accessor  && item.renderComponent === updatedColumn.renderComponent ? ({ ...updatedColumn }) : item);
    setActiveCols(updatedColumns);
    localStorage.setItem(activeStorageKey, JSON.stringify(toPersistShape(updatedColumns)));

    if (tableSortStatus.columnAccessor === updatedColumn.accessor && updatedColumn.hidden) {
      setTableSortStatus(null);
    }
  };

  const handleOpenEditColumnModal = (column: any, anchorRect?: DOMRect) => {
    setIsEditColumnModal(true);
    setSelectedColumn(column);
    setSelectedColumnAnchorRect(anchorRect);
  };

  const handleCloseEditColumnModal = () => {
    setIsEditColumnModal(false);
    setSelectedColumn(null);
    setSelectedColumnAnchorRect(undefined);
  };

  const handleOpenManageColumnsDrawer = () => {
    setIsManageColumnsDrawer(true);
  };

  const handleCloseManageColumnsDrawer = () => {
    setIsManageColumnsDrawer(false);
  };

  const handleColumnAddedAfterAdd = useCallback(async () => {
    // fetchQuery always hits the network and writes cache — avoids stale shared observers between useIdeaPages() sites
    await queryClient.fetchQuery({
      queryKey: ideaPagesKeys.all,
      queryFn: async () => {
        const response = await getIdeaPages(auth0);
        return response.ideaPages;
      },
    });
    await refetch();
    setIdeasTableLayoutKey((k) => k + 1);
    setIsManageColumnsDrawer(false);
  }, [queryClient, auth0, refetch]);

  const handleEditPage = () => {
    setEditTitle(true);
    setEditMode(true);
  };

  const handleExitEditMode = () => {
    setEditTitle(false);
    setEditMode(false);
  };

  // Exit edit mode when subpage key changes
  useEffect(() => {
    setEditMode(false);
    setEditTitle(false);
  }, [subPageKey, setEditMode]);

  return (
    <Box>
      <Box bg={"#fff"} ref={ref}>
        <Box pt={20} mx={20}>
          <Flex align="center" justify="space-between">
            <Flex justify="space-between" style={{ width: '100%' }}>
              {/* Editable Page Name */}
              {userRole === "admin" ? (
                <>
                  {editTitle && subPageKey ? (
                    <Flex align="left">
                      <Tooltip label="Edit name">
                        <TextInput
                          value={inputValue}
                          onChange={e => setInputValue(e.currentTarget.value)}
                          onBlur={handleNameSave}
                          onKeyDown={e => {
                            if (e.key === 'Enter') handleNameSave();
                          }}
                          size="sm"
                          styles={{ input: { fontSize: 20, fontWeight: 600, marginBottom: 13, width: 250 } }}
                          autoFocus
                        />
                      </Tooltip>
                    </Flex> ) : (
                    <Flex align="center">
                      <Text fz="20px"  fw={600}  mb={4}  >
                        {isArchived ? "Ideas - Archived" : inputValue || "Ideas"}
                      </Text>
                    </Flex>
                  )}
                </>
              ) : (
                <Flex align="center">
                  <Text
                    fz="20px"
                    fw={600}
                    mb={4}
                  >
                    {isArchived ? "Ideas - Archived" : inputValue || "Ideas"}
                  </Text>
                </Flex>
              )}
            </Flex>
          </Flex>
          {/* Header */}
          <Box ref={warpRef}>
            <Header
              savedViews={savedViews}
              selectedView={selectedView}
              setSelectedView={setSelectedView}
              upsertView={upsertView}
              warpWidth={warpWidth}
              isGlobal={isGlobal}
              setIsGlobal={setIsGlobal}
              isSubPageView={isSubPageView}
              setIsSubPageView={setIsSubPageView}
              actionIconsWidth={actionIconsWidth}
              isArchived={isArchived}
              toggle={toggle}
              search={search}
              setSearch={setSearch}
              searchOpened={searchOpened}
              setSearchOpened={setSearchOpened}
              actionIconsRef={actionIconsRef}
              setFilterModalOpened={setFilterModalOpened}
              settingsMenu={
                <Menu shadow="md" width={180} position="bottom-end" withinPortal>
                  <Menu.Target>
                    <Tooltip label="Page actions" withArrow>
                      <span data-testid={TEST_IDS.pageActionsButton} style={{ display: 'inline-flex', alignItems: 'center', cursor: 'pointer' }}>
                        <IconDotsVertical size={20} color="grey" />
                      </span>
                    </Tooltip>
                  </Menu.Target>
                  <Menu.Dropdown>
                    {userRole === "admin" && subPageKey && (
                      <Menu.Item
                        icon={<IconPencil size={16} />}
                        onClick={editMode ? handleExitEditMode : handleEditPage}
                      >
                        {editMode ? "Exit Edit mode" : "Edit page"}
                      </Menu.Item>
                    )}
                    {userRole === "admin" && (
                      <>
                        <Menu.Item
                          icon={<IconCopy size={16} />}
                          onClick={handleClone}
                        >
                          Duplicate Page
                        </Menu.Item>
                        <Menu.Item
                          icon={<IconTrash size={16} />}
                          onClick={openDeleteModal}
                          disabled={!subPageKey}
                        >
                          Delete Page
                        </Menu.Item>
                      </>
                    )}
                    <Menu.Item
                      icon={<IconColumns size={16} />}
                      onClick={handleOpenManageColumnsDrawer}
                    >
                      Manage Columns
                    </Menu.Item>
                    <Menu.Item
                      icon={<IconCheck size={16} />}
                      onClick={handleOpenSaveViewModal}
                      disabled={!activeFilters?.length}
                    >
                      Save as new view
                    </Menu.Item>
                  </Menu.Dropdown>
                </Menu>
              }
            />
          </Box>

          {/* Filter Bar */}
          <Flex align="center" mt="sm" mb="xs">
            <StickyFilters subPageKey={subPageKey} setFilterModalOpened={setFilterModalOpened} selectedView={selectedView} />
          </Flex>
          
          {/* Filter Actions */}
          {filterRowOpened && (
            <FilterActions
              activeFilters={activeFilters}
              setActiveFilters={setActiveFilters}
              selectedView={selectedView}
              open={open}
              handleSaveViews={handleSaveViews}
              opened={opened}
              viewName={viewName}
              setViewName={setViewName}
              isGlobal={isGlobal}
              setIsGlobal={setIsGlobal}
              isSubPageView={isSubPageView}
              setIsSubPageView={setIsSubPageView}
              doClose={doClose}
              toggle={toggle}
              search={search}
              setSearch={setSearch}
              searchOpened={searchOpened}
              setSearchOpened={setSearchOpened}
              actionIconsRef={actionIconsRef}
              sortState={sortState}
              setSortState={setSortState}
              filterModalOpened={filterModalOpened}
              setFilterModalOpened={setFilterModalOpened}
              saveViewModalOpened={saveViewModalOpened}
              setSaveViewModalOpened={setSaveViewModalOpened}
            />
            
          )}
          
          {/* Main Table Display */}
          <Box>
            {/* Wait for subpage to load before rendering table (skip for /ideas/all - use main page columns) */}
            {(subPageKey && subPageKey !== 'all' && subPageLoading) ? (
              <Stack h="66vh" w="88vw" align="center" justify="center">
                <Loader />
              </Stack>
            ) : activeColumns && activeColumns.length > 0 ? (
              <IdeasTable
                key={ideasTableLayoutKey}
                ideaColumns={activeColumns}
                setEditColumnModal={ handleOpenEditColumnModal }
                updateColumn={updateColumn}
                ideas={ideas}
                isAllowedToEditDates={isAllowedToEditDates}
                handlePageChange={(newPage: string) => setPage(newPage)}
                totalRecords={totalCount}
                recordsPerPage={PAGE_SIZE}
                page={Number(page) || 1}
                sortStatus={tableSortStatus}
                setSortStatus={setTableSortStatus}
                loading={loading}
                aggregationsLoading={aggregationsLoading}
                PAGE_SIZE={PAGE_SIZE}
                selectedView={selectedView}
                isTagsHovered={isTagsHovered}
                sortState={sortState}
                isAiPage={isAiPage}
                searchQuery={debouncedSearch}
                onColumnsReordered={handleColumnsReordered}
              />
            ) : (
              <Stack h="66vh" w="88vw" align="center" justify="center">
                <Loader />
                {/* Debug info */}
                {process.env.NODE_ENV === 'development' && (
                  <Text size="sm" color="dimmed">
                    Columns: {activeColumns?.length || 0}, IdeaColumns: {columns?.length || 0}
                  </Text>
                )}
              </Stack>
            )}
          </Box>
        </Box>
      </Box>
      { isEditColumnModal && (
        <EditColumnModal
          opened={isEditColumnModal}
          onClose={handleCloseEditColumnModal}
          column={selectedColumn}
          anchorRect={selectedColumnAnchorRect}
          updateColumn={updateColumn}
        />
      )}
      <ManageColumnsDrawer
        opened={isManageColumnsDrawer}
        onClose={handleCloseManageColumnsDrawer}
        columns={activeColumns}
        updateColumn={updateColumn}
        onSaveColumns={handleSaveColumns}
        onEditColumn={handleOpenEditColumnModal}
        originalColumns={columns}
        subPageKey={subPageKey}
        isAiView={isAiSelected}
        onColumnAdded={handleColumnAddedAfterAdd}
      />
    </Box>
  );
};

export const IdeasWraper = ({ pageKey }: { pageKey: string | null }) => {
  return (
    <FiltersContextProvider initialSubpageKey={pageKey}>
      <Ideas />
    </FiltersContextProvider>
  );
};
